import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Grid, Stepper, Step, makeStyles } from '@material-ui/core';
import StepStatus from './StepStatus';
import NavigationButton from './NavigationButton';
import AuditContext from './AuditContext';
import { connect } from 'react-redux';
import AuditStep from './AuditStep';
import { CONSTANTS } from '../../../constants/constants';
import API_REST from '../../../api-rest-uruk';
import { Auth } from 'aws-amplify';

const useStyles = makeStyles({
  stepper: {
    flex: props => props.fullWidth ? 1 : 'initial',
    padding: '8px 0',
    overflowX: 'scroll',
    scrollbarWidth: 'none',
    scrollBehavior: 'smooth',
    '&::-webkit-scrollbar': {
      display: 'none'
    }
  },
  step: {
    flex: 'initial'
  }
});

const FlowSteps = ({
  document,
  userId,
  user,
  showNotifications = true,
  skip = 1,
  nonLinear = false,
  fullWidth = false,
  markAsRead
}) => {
  const { signInUserSession: { idToken: { payload: { email, phone_number: phone } } } } = user;
  const styles = useStyles({ fullWidth });
  const stepperRef = useRef();
  const [applicationType, setApplicationType] = useState();

  const [isScrollable, setIsScrollable] = useState({ left: false, right: false });
  const [context, setContext] = useState(null);
  const flow = typeof document.flow === 'string'
    ? JSON.parse(document.flow)
    : document.flow;

  const steps = showNotifications
    ? flow.steps
    : flow.steps.filter(step => step.type !== 'notificationStep');
  const activeStep = steps.findIndex(step => ![CONSTANTS.STEP.STATUS.INIT, CONSTANTS.STEP.STATUS.SIGNED].includes(step.status));

  useEffect(() => {
    const availableSize = stepperRef.current.clientWidth;
    const elementSize = stepperRef.current.scrollWidth;
    if (elementSize > availableSize) {
      setIsScrollable({ ...isScrollable, right: true });
    }

    if (activeStep > 3) {
      stepperRef.current.scrollLeft += 96 * activeStep;
    }

    const getApplicationType = async () => {
      const currentUser = await Auth.currentSession();
      setApplicationType(currentUser.getIdToken().payload.applicationType);
    };
    getApplicationType();
  }, []);

  const onScroll = () => {
    const scrollLeft = stepperRef.current.scrollLeft;
    const scrollWidth = stepperRef.current.scrollWidth;
    const clientWidth = stepperRef.current.clientWidth;

    if (scrollLeft === 0) {
      setIsScrollable({ ...isScrollable, left: false });
    } else if (Math.ceil(clientWidth + scrollLeft) >= scrollWidth) {
      setIsScrollable({ ...isScrollable, right: false });
    } else if (scrollLeft > 0) {
      setIsScrollable({ right: true, left: true });
    }
  };

  const relaunchStep = async (step) => {
    const body = {
      id: document.id,
      type: document.type,
      step
    };
    return API_REST.relaunchStep(body);
  };

  const handleBack = () => {
    stepperRef.current.scrollLeft -= 96 * skip;
  };

  const handleNext = () => {
    stepperRef.current.scrollLeft += 96 * skip;
  };

  return (
    <AuditContext.Provider value={{ step: context, setStep: setContext }}>
      <AuditStep flow={flow} />
      <Grid
        container
        wrap='nowrap'
        justify='center'
      >
        <NavigationButton
          onClick={handleBack}
          disabled={!isScrollable.left}
        />
        <Stepper
          ref={stepperRef}
          activeStep={activeStep}
          alternativeLabel
          nonLinear={nonLinear}
          className={styles.stepper}
          onScroll={onScroll}
        >
          {steps.map(step => (
            <Step
              key={step.stepId || step.order}
              className={styles.step}
            >
              <StepStatus
                step={step}
                flow={flow}
                applicationType={applicationType}
                userId={userId}
                email={email}
                phone={phone}
                onRelaunch={relaunchStep}
              />
            </Step>
          ))}
        </Stepper>
        <NavigationButton
          type='next'
          onClick={handleNext}
          disabled={!isScrollable.right}
        />
      </Grid>
    </AuditContext.Provider>
  );
};

FlowSteps.propTypes = {
  document: PropTypes.shape({
    id: PropTypes.string,
    flow: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
    type: PropTypes.string
  }).isRequired,
  userId: PropTypes.string.isRequired,
  showNotifications: PropTypes.bool,
  skip: PropTypes.number,
  nonLinear: PropTypes.bool,
  fullWidth: PropTypes.bool,
  markAsRead: PropTypes.bool
};

const mapStateToProps = (state) => {
  return {
    userId: state.user.username || state.config.user.username,
    user: state.config.user || state.user
  };
};

export default connect(mapStateToProps, null)(FlowSteps);
